/**
 * One engagement prompt, open in front of a donor: a poll, a raffle, a question queue, a comment
 * wall (GH-781).
 *
 * ## Why it is its own file now
 *
 * It was a private function inside `DonorPanel.tsx`, reachable only by opening the hamburger sheet
 * on the directory. The donor's event page needs the same four kinds rendered inside an action
 * card, on the page, without navigating away — and a second implementation of "the poll's tallies
 * appear only after you have voted" is a second chance to get that rule wrong. So the component
 * moved out unchanged and now has two hosts: the sheet, and the event page's expanded card.
 *
 * ## The rules inside here that are not styling
 *
 *  - **Tallies are the server's decision, not this component's.** `detail.tallies` is null until
 *    the donor has voted or the poll has closed. Rendering counts from `own` while it is null
 *    would let a client preview a vote it has not cast, which is exactly the bias the server-side
 *    rule exists to prevent.
 *  - **The raffle stub is stable.** Entering twice returns the same `#N` because the server never
 *    issues one donor two numbers; this screen shows the number rather than a "you are entered"
 *    that a reload could turn into a second entry in somebody's head.
 *  - **"Don't show my name onscreen" resets after every send.** It is a decision about one
 *    question, not a preference about the donor — the same person may ask one thing openly and
 *    the next thing not.
 *  - **It polls while it is open and stops when it closes.** Five seconds, cleared on unmount.
 *    The event page keeps one card expanded at a time for this reason: four open cards would be
 *    four requests every five seconds from a phone on a hall's wifi.
 *
 * Every action here needs a checked-in donor, and the server is what enforces that
 * (`requireCheckedIn` on detail, submissions and upvote). Nothing in this file asserts presence;
 * it carries the session token and renders the refusal if one comes back.
 */
import { useCallback, useEffect, useState } from "react";
import { useFlip } from "@givehere/ui";
import {
  ANONYMITY_COPY,
  ENGAGEMENT_COPY,
  type EngagementDetailResponse,
  type EngagementPromptWire,
} from "@givehere/contracts";
import {
  DonorApiError,
  fetchPromptDetail,
  submitToPrompt,
  upvoteQuestion,
} from "./donor-session.js";
import { Explain, ExplainMore } from "./Explain.js";
import { ReportMenu } from "./report/ReportMenu.js";
import { ReportNotice } from "./report/ReportNotice.js";
import { useReporting } from "./report/useReporting.js";

export function PromptView({
  token,
  prompt,
  onBack,
  backLabel = "\u2190 Back",
  showTitle = true,
  eventId = null,
}: {
  token: string;
  prompt: EngagementPromptWire;
  onBack: () => void;
  /**
   * What the control that leaves this view says. The sheet pushes this over its menu, so there
   * it is "← Back"; an event-page card expands in place and nothing was navigated away from, so
   * there it says "Close". A control whose words describe a journey the donor did not take is a
   * small lie the screen tells on every tap.
   */
  backLabel?: string;
  /**
   * Whether to draw the prompt's own title. False on the event page, where the card row a donor
   * just pressed already carries it — a heading repeating the row above it is a wasted line on
   * the screen this product has the least of, and a duplicate announcement to a screen reader.
   */
  showTitle?: boolean;
  /**
   * The event the prompt belongs to (GH-958). With it, other people's comments and questions get a
   * Report control and anything this reader reported stays hidden; without it neither appears.
   */
  eventId?: string | null;
}) {
  const reporting = useReporting(eventId);
  const [detail, setDetail] = useState<EngagementDetailResponse | null>(null);
  const [text, setText] = useState("");
  /**
   * Ask without your name on the big screen (owner-directed 2026-08-22).
   *
   * Off by default. Named is what this screen has always done, and flipping the default would
   * quietly anonymise everyone who was happy to be named.
   */
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The organizer took this off the room (GH-878): Live control's Hide, or Archive. The server
   * answers `not_found` for every read and write of it from then on, so the view stops drawing the
   * prompt — its questions, its field, its buttons — and says so in the server's words. The poll
   * keeps asking, so Show brings it straight back.
   */
  const [gone, setGone] = useState<string | null>(null);

  const refresh = useCallback(() => {
    fetchPromptDetail(token, prompt.id)
      .then((fresh) => {
        setDetail(fresh);
        setGone(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DonorApiError && caught.code === "not_found") {
          setDetail(null);
          setGone(caught.message);
          return;
        }
        setError(caught instanceof DonorApiError ? caught.message : "Could not load this.");
      });
  }, [token, prompt.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * The room is live: tallies climb, questions rise, comments appear. A gentle poll keeps this
   * view honest without the donor pulling to refresh — and stops the moment the view closes.
   */
  useEffect(() => {
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const raise = (submissionId: string) => {
    upvoteQuestion(token, submissionId)
      .then(refresh)
      .catch((caught: unknown) =>
        setError(caught instanceof DonorApiError ? caught.message : "Could not do that."),
      );
  };

  const send = (body: { choice?: string; body?: string; anonymous?: boolean }) => {
    setBusy(true);
    setError(null);
    submitToPrompt(token, prompt.id, body)
      .then(() => {
        setText("");
        // Reset with the draft: this is a decision about one question, not a preference. The
        // same person may ask one thing openly and the next thing not.
        setAnonymous(false);
        refresh();
      })
      .catch((caught: unknown) =>
        setError(caught instanceof DonorApiError ? caught.message : "Could not send that."),
      )
      .finally(() => setBusy(false));
  };

  const current = detail?.prompt ?? prompt;
  const own = detail?.own ?? [];
  const closed = current.status === "closed";

  // Rows re-rank by votes on every refresh; animating them to their new place (instead of
  // snapping under the reader's thumb) is what keeps the list readable while it moves.
  const questionsRef = useFlip<HTMLUListElement>([detail?.questions]);

  return (
    <>
      <button type="button" className="link" onClick={onBack}>
        {backLabel}
      </button>
      {showTitle && (
        <h2 id="donor-title" className="donor__title" dir="auto">
          {current.title}
        </h2>
      )}

      {current.resultSummary && (
        <p className="notice">
          <strong>{current.resultSummary}</strong>
        </p>
      )}

      {gone && <p className="notice">{gone}</p>}

      {!detail && !error && !gone && <p className="notice">Loading…</p>}

      {detail && current.kind === "raffle" && (
        <>
          {current.prize && <p className="donor__where">Prize: {current.prize}</p>}
          {own.length > 0 ? (
            // The ticket number is the donor's stub. It is stable: re-entering or reloading
            // shows the same #N, because the server never issues one donor two numbers.
            <p className="notice">
              {own[0]?.entryNumber !== null && own[0]?.entryNumber !== undefined ? (
                <>
                  You have been entered as <strong>#{own[0].entryNumber}</strong>. Good luck!
                </>
              ) : (
                "You are entered. Good luck!"
              )}
            </p>
          ) : closed ? (
            <p className="notice">Entries are closed.</p>
          ) : (
            <button type="button" className="pay" disabled={busy} onClick={() => send({})}>
              {busy ? "Entering…" : "Enter the raffle"}
            </button>
          )}
        </>
      )}

      {detail && current.kind === "poll" && (
        <>
          {detail.tallies === null ? (
            <div className="donor__choices">
              {(current.choices ?? []).map((choice) => (
                <button
                  key={choice}
                  type="button"
                  className="method"
                  disabled={busy || closed}
                  onClick={() => send({ choice })}
                >
                  {choice}
                </button>
              ))}
              {closed && <p className="notice">Voting is closed.</p>}
            </div>
          ) : (
            <ul className="donor__tallies">
              {detail.tallies.map((tally) => {
                // The donor's own ballot is in `own`; marking it answers "did that count?"
                // without any new request.
                const mine = own.some((response) => response.choice === tally.choice);
                return (
                  <li key={tally.choice} className={mine ? "donor__tally--mine" : undefined}>
                    <span>
                      {tally.choice}
                      {mine && <small className="donor__mine-mark"> ✓ your vote</small>}
                    </span>
                    <strong>{tally.count}</strong>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {detail && (current.kind === "question" || current.kind === "comment_wall") && (
        <>
          {current.kind === "comment_wall" && detail.comments && detail.comments.length > 0 && (
            <ul className="donor__comments">
              {detail.comments
                .filter((comment) => !comment.id || reporting.isShown(comment.id))
                .map((comment, index) => (
                  <li key={comment.id ?? index} className="report__row">
                    <span>
                      <span>{comment.body}</span>
                      {comment.displayName && <small>— {comment.displayName}</small>}
                    </span>
                    {reporting.eventId && comment.id && (
                      <ReportMenu
                        eventId={reporting.eventId}
                        target={{ kind: "comment", submissionId: comment.id }}
                        itemKey={comment.id}
                        onReported={reporting.onReported}
                      />
                    )}
                  </li>
                ))}
            </ul>
          )}
          {current.kind === "comment_wall" && detail.comments?.length === 0 && (
            <p className="notice">No comments yet</p>
          )}

          {current.kind === "question" && detail.questions && detail.questions.length > 0 && (
            <ul className="donor__questions" ref={questionsRef}>
              {detail.questions
                .filter((question) => reporting.isShown(question.id))
                .map((question) => (
                  <li
                    key={question.id}
                    data-flip-key={question.id}
                    className={
                      question.answering
                        ? "question question--answering"
                        : question.answered
                          ? "question question--answered"
                          : "question"
                    }
                  >
                    <button
                      type="button"
                      className={question.mine ? "upvote upvote--mine" : "upvote"}
                      aria-pressed={question.mine}
                      aria-label={`Raise this question (${question.votes})`}
                      disabled={closed}
                      onClick={() => raise(question.id)}
                    >
                      ▲ {question.votes}
                    </button>
                    <span className="question__body">
                      {/* GH-878: the one the speaker is on, marked from the console — said in
                        words, on the selected tint, so it never rests on the colour. */}
                      {question.answering && (
                        <small className="question__now">{ENGAGEMENT_COPY.answeringNow}</small>
                      )}
                      {question.body}
                      {question.displayName && <small> — {question.displayName}</small>}
                      {question.answered && <small className="question__done"> ✓ answered</small>}
                    </span>
                    {reporting.eventId && (
                      <ReportMenu
                        eventId={reporting.eventId}
                        target={{ kind: "question", submissionId: question.id }}
                        itemKey={question.id}
                        onReported={reporting.onReported}
                      />
                    )}
                  </li>
                ))}
            </ul>
          )}
          {reporting.eventId && <ReportNotice thanks={reporting.thanks} />}
          {own.length > 0 && current.kind === "question" && (
            <p className="donor__where">
              Sent: “{own[own.length - 1]?.body}” — it appears here once the organizers approve it.
            </p>
          )}
          {own.length > 0 && current.kind === "comment_wall" && (
            <Explain
              className="donor__where"
              label="Organizers approve comments"
              title="Before it appears"
            >
              <p>Comments appear once the organizers approve them.</p>
            </Explain>
          )}

          {!closed && (
            <>
              {/* Visible label, not a placeholder: placeholders vanish on focus and low-vision
                  readers lose the field's purpose mid-typing (HIG). */}
              <label className="field">
                <span>{current.kind === "question" ? "Your question" : "Your comment"}</span>
                <input
                  maxLength={1000}
                  value={text}
                  onChange={(changed) => setText(changed.target.value)}
                />
              </label>
              {/* Directly under the field it applies to, because it is part of writing the
                  question rather than a setting about the donor. The label says what happens
                  rather than naming the setting: "anonymous" alone leaves a reader guessing
                  whether it hides them from the room, the organization, or both.

                  Trimmed to five words on 2026-08-31, and **"onscreen" is the word that could not
                  go**: it is the whole disambiguation. "Don't show my name" on its own is the
                  ambiguous label this one was written to replace, so the cut took "on the" and
                  kept the noun rather than the other way round. */}
              {/* The ⓘ beside it says the other half (GH-858, DEC-049): the host still sees who
                  sent it. A separate control, so tapping it never ticks the box. */}
              <div className="field-check-row">
                <label className="field field--check">
                  <input
                    type="checkbox"
                    checked={anonymous}
                    onChange={(changed) => setAnonymous(changed.target.checked)}
                  />
                  <span>Don’t show my name onscreen</span>
                </label>
                <ExplainMore title={ANONYMITY_COPY.publicExplanationTitle}>
                  <p>{ANONYMITY_COPY.promptExplanation}</p>
                </ExplainMore>
              </div>
              <button
                type="button"
                className="pay"
                disabled={busy || text.trim().length === 0}
                onClick={() => send({ body: text.trim(), anonymous })}
              >
                {busy ? "Sending…" : "Send"}
              </button>
            </>
          )}
        </>
      )}

      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
